import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Image,
  SimpleGrid,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { MdArrowBack, MdAutoAwesome, MdDocumentScanner, MdEdit, MdNotificationsNone, MdWarningAmber, MdVerifiedUser } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AppCard,
  FileViewerModal,
  OutlineButton,
  PrimaryButton,
  StatusAlertCard,
  StatusBadge,
  PageLoader,
} from '../../../../components/ui';
import {
  EQUIPMENT_DOC_TYPES,
  EQUIPMENT_FIELD_KEYS,
  isPlaceholderCustomDocKey,
} from '../../../../data/master/equipments';
import { uploadUserEquipmentDocument } from '../../../../repositories/document.repository';
import {
  extractUserEquipmentData,
  getUserEquipment,
} from '../../../../repositories/userEquipment.repository';
import { getComplianceExpiryAlerts } from '../../../../utils/equipmentCompliance';
import { formatDateTimeDisplay } from '../../../../utils/personName';
import { SendNotificationModal } from '../../users/SendNotificationModal';
import { EquipmentAiVerifyModal } from './EquipmentAiVerifyModal';
import { EquipmentEditModal } from './EquipmentEditModal';
import { EquipmentKycStatusModal } from './EquipmentKycStatusModal';

const PHOTO_SLOTS = [
  { key: 'photo_front', label: 'Front' },
  { key: 'photo_back', label: 'Back' },
  { key: 'photo_left', label: 'Left' },
  { key: 'photo_right', label: 'Right' },
];

const PHOTO_KEY_SET = new Set(PHOTO_SLOTS.map((s) => s.key));

function humanizeKey(key) {
  return String(key || '')
    .replace(/^c_doc_/i, 'doc ')
    .replace(/_/g, ' ')
    .trim();
}

/** Non-photo document checklist — skips side photos and unnamed placeholder keys. */
function resolvePaperDocList(asset) {
  const docs = asset?.documents || {};
  let list = [];

  if (Array.isArray(asset?.documentTypes) && asset.documentTypes.length) {
    list = asset.documentTypes.map((d) => ({
      key: d.key,
      label: d.label || humanizeKey(d.key),
    }));
  } else {
    const fromDocs = Object.keys(docs).filter((k) => !PHOTO_KEY_SET.has(k));
    if (fromDocs.length) {
      list = fromDocs.map((key) => {
        const meta = EQUIPMENT_DOC_TYPES.find((d) => d.key === key);
        return { key, label: meta?.label || humanizeKey(key) };
      });
    } else {
      list = EQUIPMENT_DOC_TYPES.filter((d) => !PHOTO_KEY_SET.has(d.key));
    }
  }

  return list.filter(({ key }) => {
    if (PHOTO_KEY_SET.has(key)) return false;
    // Drop leftover "Add document" placeholders (e.g. c_doc_1) unless they alone hold a file.
    if (isPlaceholderCustomDocKey(key) && !docs[key]) return false;
    return true;
  });
}

function equipmentAgeYears(yom) {
  const year = Number.parseInt(String(yom || '').trim(), 10);
  if (!Number.isFinite(year) || year < 1900 || year > 2100) return null;
  return new Date().getFullYear() - year;
}

function fileKindFromUrl(url, fileName = '') {
  const hay = `${url || ''} ${fileName || ''}`.toLowerCase();
  if (hay.includes('.pdf') || hay.includes('application/pdf')) return 'pdf';
  return 'image';
}

function FieldCard({ label, value, alert = false, expired = false, alertLabel = '' }) {
  const valueColor = expired
    ? 'status.error'
    : alert
      ? 'status.warning'
      : undefined;

  const card = (
    <Box
      px={3}
      py={2}
      bg={alert ? 'bg.amberTint' : 'bg.input'}
      borderRadius="sm"
      borderWidth="1px"
      borderColor={alert ? 'utkarsh.amber' : 'border.subtle'}
      w="100%"
      cursor={alert ? 'help' : undefined}
      transition="border-color 0.15s ease, box-shadow 0.15s ease"
      _hover={
        alert
          ? {
              borderColor: expired ? 'status.error' : 'brand.600',
              boxShadow: 'sm',
            }
          : undefined
      }
    >
      <Text fontSize="12px" color="text.secondary" mb={0.5} fontWeight="500">
        {label}
      </Text>
      <Text
        fontSize="15px"
        fontWeight="600"
        whiteSpace="pre-wrap"
        color={valueColor}
      >
        {value || '—'}
      </Text>
    </Box>
  );

  if (!alert || !alertLabel) return card;

  return (
    <Tooltip
      label={alertLabel}
      hasArrow
      placement="top"
      openDelay={200}
      bg={expired ? 'status.error' : 'utkarsh.ink'}
      color="white"
      fontSize="13px"
      px={3}
      py={2}
      borderRadius="md"
    >
      {card}
    </Tooltip>
  );
}

function kycBadge(status) {
  if (status === 'Verified') return { status: 'verified' };
  if (status === 'Rejected') return { status: 'error' };
  return { status: 'pending' };
}

function CompactDocRow({ label, url, busy, onOpen, onUpload }) {
  const canOpen = Boolean(url);
  return (
    <Flex
      align="center"
      gap={2}
      px={2}
      py={1.5}
      bg="bg.input"
      borderRadius="sm"
      borderWidth="1px"
      borderColor="border.subtle"
      cursor={canOpen ? 'pointer' : 'default'}
      role={canOpen ? 'button' : undefined}
      tabIndex={canOpen ? 0 : undefined}
      onClick={() => {
        if (canOpen) onOpen?.();
      }}
      onKeyDown={(e) => {
        if (!canOpen) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen?.();
        }
      }}
      _hover={canOpen ? { borderColor: 'utkarsh.amber' } : undefined}
    >
      <Box
        w="36px"
        h="36px"
        borderRadius="sm"
        overflow="hidden"
        bg="white"
        flexShrink={0}
        borderWidth="1px"
        borderColor="border.subtle"
        pointerEvents="none"
      >
        {url && fileKindFromUrl(url) === 'image' ? (
          <Image src={url} alt={label} w="100%" h="100%" objectFit="cover" />
        ) : (
          <Flex w="100%" h="100%" align="center" justify="center">
            <Text fontSize="10px" color="text.secondary">
              {url ? 'PDF' : '—'}
            </Text>
          </Flex>
        )}
      </Box>
      <Box flex="1" minW={0} pointerEvents="none">
        <Text fontWeight="600" fontSize="13px" noOfLines={1}>
          {label}
        </Text>
        <Text fontSize="11px" color="text.secondary">
          {url ? 'Uploaded · click to open' : 'Not uploaded'}
        </Text>
      </Box>
      <OutlineButton
        size="xs"
        flexShrink={0}
        isLoading={busy}
        onClick={(e) => {
          e.stopPropagation();
          onUpload?.();
        }}
      >
        {url ? 'Update' : 'Upload'}
      </OutlineButton>
    </Flex>
  );
}

export default function EquipmentFleetDetailPage() {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const kycModal = useDisclosure();
  const editModal = useDisclosure();
  const aiVerifyModal = useDisclosure();
  const notifyModal = useDisclosure();
  const fileViewer = useDisclosure();
  const fileRef = useRef(null);
  const pendingDocKeyRef = useRef(null);

  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [viewerFile, setViewerFile] = useState(null);
  const [busyKey, setBusyKey] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getUserEquipment(assetId);
      setAsset(data);
    } catch (err) {
      toast({
        title: err?.message || 'Failed to load equipment',
        status: 'error',
        position: 'top-right',
      });
      setAsset(null);
    } finally {
      setLoading(false);
    }
  }, [assetId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const ageYears = useMemo(() => equipmentAgeYears(asset?.yom), [asset?.yom]);
  const paperDocs = useMemo(() => (asset ? resolvePaperDocList(asset) : []), [asset]);
  const complianceAlerts = useMemo(
    () => getComplianceExpiryAlerts(asset),
    [asset],
  );
  const extractedItems = useMemo(() => {
    const items = asset?.extractedData?.items;
    return Array.isArray(items) ? items : [];
  }, [asset?.extractedData]);
  const extractedAt = asset?.extractedData?.extractedAt || null;

  const runExtractData = async () => {
    if (!asset?.id || extracting) return;
    setExtracting(true);
    try {
      const data = await extractUserEquipmentData(asset.id);
      setAsset((prev) =>
        prev
          ? {
              ...prev,
              extractedData:
                data && typeof data === 'object'
                  ? data
                  : prev.extractedData,
            }
          : prev,
      );
      toast({
        title: 'Extraction complete',
        description: `${Array.isArray(data?.items) ? data.items.length : 0} field(s) found`,
        status: 'success',
        position: 'top-right',
      });
      await load();
    } catch (err) {
      toast({
        title: err?.message || 'AI extraction failed',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setExtracting(false);
    }
  };

  const pickFile = (docKey) => {
    pendingDocKeyRef.current = docKey;
    if (fileRef.current) {
      fileRef.current.accept = 'image/*,application/pdf';
      fileRef.current.click();
    }
  };

  const handleFileChosen = async (e) => {
    const file = e.target.files?.[0];
    const docType = pendingDocKeyRef.current;
    e.target.value = '';
    pendingDocKeyRef.current = null;
    if (!file || !docType || !asset?.id) return;

    setBusyKey(docType);
    try {
      await uploadUserEquipmentDocument({
        equipmentId: asset.id,
        docType,
        file,
      });
      toast({
        title: 'Document saved',
        status: 'success',
        position: 'top-right',
      });
      await load();
    } catch (err) {
      toast({
        title: err?.message || 'Upload failed',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setBusyKey('');
    }
  };

  const openDoc = (docType, url, label) => {
    if (!url) return;
    const kind = fileKindFromUrl(url);
    setViewerFile({
      id: `${asset.id}-${docType}`,
      url,
      label: label || docType,
      fileName: `${docType}.${kind === 'pdf' ? 'pdf' : 'jpg'}`,
      kind,
      mimeType: kind === 'pdf' ? 'application/pdf' : 'image/jpeg',
    });
    fileViewer.onOpen();
  };

  if (loading) return <PageLoader label="Loading equipment…" />;
  if (!asset) {
    return (
      <Box>
        <OutlineButton leftIcon={<MdArrowBack />} onClick={() => navigate('/admin/equipments/fleet')}>
          Back to fleet
        </OutlineButton>
        <Text mt={6} color="text.secondary">
          Equipment not found.
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <input
        ref={fileRef}
        type="file"
        accept="image/*,application/pdf"
        hidden
        onChange={handleFileChosen}
      />

      <Flex align="center" gap={3} mb={5} wrap="wrap">
        <Box flex="1" minW={0}>
          <Heading size="md" noOfLines={1}>
            {asset.equipmentType || 'Equipment'}
          </Heading>
          <Text fontSize="13px" color="text.secondary">
            {[asset.ownerName, asset.ownerPhone, asset.villageName].filter(Boolean).join(' · ')}
          </Text>
        </Box>
        <StatusBadge {...kycBadge(asset.status)}>{asset.status}</StatusBadge>
        <OutlineButton
          size="sm"
          h="40px"
          minH="40px"
          leftIcon={<MdAutoAwesome />}
          onClick={aiVerifyModal.onOpen}
        >
          Verify
        </OutlineButton>
        <OutlineButton
          size="sm"
          h="40px"
          minH="40px"
          leftIcon={<MdDocumentScanner />}
          onClick={runExtractData}
          isLoading={extracting}
          title="Extract fields from documents & photos"
        >
          Extract data
        </OutlineButton>
        <OutlineButton
          size="sm"
          h="40px"
          minH="40px"
          leftIcon={<MdEdit />}
          onClick={editModal.onOpen}
        >
          Edit details
        </OutlineButton>
        {asset.userId ? (
          <OutlineButton
            size="sm"
            h="40px"
            minH="40px"
            leftIcon={<MdNotificationsNone />}
            onClick={notifyModal.onOpen}
          >
            Send notification
          </OutlineButton>
        ) : null}
        <PrimaryButton
          size="sm"
          h="40px"
          minH="40px"
          leftIcon={<MdVerifiedUser />}
          onClick={kycModal.onOpen}
        >
          Update status
        </PrimaryButton>
      </Flex>

      <VStack align="stretch" spacing={3} mb={4}>
        {asset.status === 'Rejected' && asset.rejectionReason ? (
          <StatusAlertCard
            variant="error"
            title="Rejected"
            body={asset.rejectionReason}
          />
        ) : null}

        {ageYears != null && ageYears >= 5 ? (
          <StatusAlertCard
            variant="pending"
            icon={MdWarningAmber}
            title="Equipment older than 5 years"
            body={`Year of manufacture is ${asset.yom} (${ageYears} years old). Review fitness, insurance, and related documents carefully before approving.`}
          />
        ) : null}
      </VStack>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4} mb={4} alignItems="start">
        <AppCard p={4}>
          <Text fontSize="14px" fontWeight="700" mb={3}>
            Details
          </Text>
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={2}>
            <FieldCard label="Type" value={asset.equipmentType} />
            <FieldCard label="Category" value={asset.equipmentTypeCategory} />
            <FieldCard label="Vehicle class" value={asset.vehicleClass} />
            {EQUIPMENT_FIELD_KEYS.filter(
              ({ key }) => asset.fieldRequirements?.[key] !== 'hidden'
            ).map(({ key, label }) => {
              const alert = complianceAlerts.find((a) => a.key === key);
              return (
                <FieldCard
                  key={key}
                  label={label}
                  value={asset[key]}
                  alert={Boolean(alert)}
                  expired={Boolean(alert?.isExpired)}
                  alertLabel={alert?.shortMessage || alert?.message || ''}
                />
              );
            })}
          </SimpleGrid>
        </AppCard>

        <AppCard p={4}>
          <Text fontSize="14px" fontWeight="700" mb={1}>
            Documents
          </Text>
          <Text fontSize="12px" color="text.secondary" mb={2}>
            Click any photo or document to open. Use Upload / Update to replace files.
          </Text>

          <SimpleGrid columns={2} spacing={2} mb={3}>
            {PHOTO_SLOTS.map(({ key, label }) => {
              const url = asset.documents?.[key];
              const busy = busyKey === key;
              return (
                <Box
                  key={key}
                  position="relative"
                  overflow="hidden"
                  bg="bg.input"
                  borderRadius="sm"
                  borderWidth="1px"
                  borderColor="border.subtle"
                  h="172px"
                  cursor={busy ? 'wait' : 'pointer'}
                  role="button"
                  tabIndex={0}
                  aria-label={url ? `Open ${label} photo` : `Upload ${label} photo`}
                  onClick={() => {
                    if (busy) return;
                    if (url) openDoc(key, url, `Photo — ${label}`);
                    else pickFile(key);
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    e.preventDefault();
                    if (busy) return;
                    if (url) openDoc(key, url, `Photo — ${label}`);
                    else pickFile(key);
                  }}
                  _hover={{ borderColor: 'utkarsh.amber' }}
                >
                  {url ? (
                    <Image
                      src={url}
                      alt={label}
                      w="100%"
                      h="100%"
                      objectFit="cover"
                      opacity={busy ? 0.6 : 1}
                      pointerEvents="none"
                    />
                  ) : (
                    <Flex
                      w="100%"
                      h="100%"
                      align="center"
                      justify="center"
                      direction="column"
                      gap={1}
                      pointerEvents="none"
                    >
                      <Text fontSize="18px" fontWeight="500" color="text.secondary" lineHeight="1">
                        +
                      </Text>
                      <Text fontSize="11px" color="text.secondary">
                        {busy ? 'Uploading…' : 'Upload'}
                      </Text>
                    </Flex>
                  )}
                  {url ? (
                    <Box position="absolute" top={1.5} right={1.5} zIndex={1}>
                      <OutlineButton
                        size="xs"
                        bg="blackAlpha.800"
                        color="white"
                        borderColor="whiteAlpha.500"
                        _hover={{ bg: 'blackAlpha.900', color: 'white' }}
                        _active={{ bg: 'blackAlpha.900', color: 'white' }}
                        isLoading={busy}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!busy) pickFile(key);
                        }}
                      >
                        Update
                      </OutlineButton>
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </SimpleGrid>

          <VStack align="stretch" spacing={1.5}>
            {paperDocs.length ? (
              paperDocs.map(({ key, label }) => {
                const url = asset.documents?.[key];
                return (
                  <CompactDocRow
                    key={key}
                    label={label}
                    url={url}
                    busy={busyKey === key}
                    onOpen={() => openDoc(key, url, label)}
                    onUpload={() => pickFile(key)}
                  />
                );
              })
            ) : (
              <Text fontSize="13px" color="text.secondary">
                No additional documents configured for this type.
              </Text>
            )}
          </VStack>
        </AppCard>
      </SimpleGrid>

      <AppCard p={4} mb={4}>
        <Flex align="baseline" justify="space-between" gap={2} mb={1} wrap="wrap">
          <Text fontSize="14px" fontWeight="700">
            Extracted Data (AI)
          </Text>
          {extractedAt ? (
            <Text fontSize="12px" color="text.secondary">
              {formatDateTimeDisplay(extractedAt)}
            </Text>
          ) : null}
        </Flex>
        {extractedItems.length === 0 ? (
          <Text fontSize="13px" color="text.secondary" py={2}>
            No extracted data yet — use Extract data.
          </Text>
        ) : (
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={2} mt={2}>
            {extractedItems.map((item) => (
              <FieldCard
                key={item.key || item.keyUi}
                label={item.keyUi || item.key || 'Field'}
                value={item.value}
              />
            ))}
          </SimpleGrid>
        )}
      </AppCard>

      <EquipmentAiVerifyModal
        isOpen={aiVerifyModal.isOpen}
        onClose={aiVerifyModal.onClose}
        asset={asset}
      />

      <EquipmentEditModal
        isOpen={editModal.isOpen}
        onClose={editModal.onClose}
        asset={asset}
        onSaved={(updated) => {
          if (updated) setAsset(updated);
          else load();
        }}
      />

      <EquipmentKycStatusModal
        isOpen={kycModal.isOpen}
        onClose={kycModal.onClose}
        asset={asset}
        onSaved={load}
        useMemberEquipment
      />

      <SendNotificationModal
        isOpen={notifyModal.isOpen}
        onClose={notifyModal.onClose}
        user={
          asset.userId
            ? {
                id: asset.userId,
                fullName: asset.ownerName || 'Member',
                userTypeLabel: 'Equipment owner',
              }
            : null
        }
      />

      <FileViewerModal
        isOpen={fileViewer.isOpen}
        onClose={() => {
          fileViewer.onClose();
          setViewerFile(null);
        }}
        file={viewerFile}
      />
    </Box>
  );
}
